import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import ViewType from "~/@types/viewType";
import { AddItemInput, ItemStateData, ItemSummaryDocument } from "~/gql/graphqlBase.generated";
import { ContainerConfigSummaryFragment } from "~/gql/queries/restock/loadList.generated";
import { SkuUpcMappingFragment } from "~/gql/queries/skuUpcMappings.generated";

export interface ItemConfigCategoryOption {
	eaches: number;
	id: string;
	name: string;
}

interface ProductState {
	addItemInput?: AddItemInput;
	categoryFormOpen: boolean;
	configCategoryFormOpen: boolean;
	configurationRowFormOpen: boolean;
	containerFormOpen: boolean;
	currentItem?: ItemStateData;
	customerItemFormOpen: boolean;
	itemConfigCategoriesData: ItemConfigCategoryOption[];
	itemConfigCategoryIds: string[];
	itemCountFormOpen: boolean;
	itemFormOpen: boolean;
	itemHandlingUnitMeasure: string;
	selectedContainerCode: null | string;
	selectedContainerConfig: ContainerConfigSummaryFragment | null;
	selectedCustomerItem?: SkuUpcMappingFragment;
	selectedItem: ItemSummaryDocument | null;
	viewType: ViewType;
}

const initialState: ProductState = {
	categoryFormOpen: false,
	configCategoryFormOpen: false,
	configurationRowFormOpen: false,
	containerFormOpen: false,
	customerItemFormOpen: false,
	itemConfigCategoriesData: [],
	itemConfigCategoryIds: [],
	itemCountFormOpen: false,
	itemFormOpen: false,
	itemHandlingUnitMeasure: "",
	selectedContainerCode: null,
	selectedContainerConfig: null,
	selectedItem: null,
	viewType: "grid"
};

export const productSlice = createSlice({
	initialState,
	name: "product",
	reducers: {
		closeCategoryForm: (state) => {
			state.categoryFormOpen = false;
		},
		closeConfigCategoryForm: (state) => {
			state.configCategoryFormOpen = false;
		},
		closeConfigurationRowForm: (state) => {
			state.configurationRowFormOpen = false;
			state.selectedContainerConfig = null;
		},
		closeContainerForm: (state) => {
			state.containerFormOpen = false;
			state.selectedContainerConfig = null;
		},
		closeCustomerItemForm: (state) => {
			state.customerItemFormOpen = false;
			state.selectedCustomerItem = undefined;
		},
		closeItemCountForm: (state) => {
			state.itemCountFormOpen = false;
		},
		closeItemForm: (state) => {
			state.itemFormOpen = false;
		},
		openCategoryForm: (state) => {
			state.categoryFormOpen = true;
		},
		openConfigCategoryForm: (state) => {
			state.configCategoryFormOpen = true;
		},
		openConfigurationRowForm: (state) => {
			state.configurationRowFormOpen = true;
		},
		openContainerForm: (state) => {
			state.containerFormOpen = true;
		},
		openCustomerItemForm: (state) => {
			state.customerItemFormOpen = true;
		},
		openItemCountForm: (state) => {
			state.itemCountFormOpen = true;
		},
		openItemForm: (state) => {
			state.itemFormOpen = true;
		},
		setAddItemInput: (state, action: PayloadAction<AddItemInput | null>) => {
			if (action.payload === null) {
				state.addItemInput = undefined;
				return;
			}
			state.addItemInput = { ...action.payload };
		},
		setCurrentItem: (state, action: PayloadAction<ItemStateData | null>) => {
			if (action.payload === null) {
				state.currentItem = undefined;
				return;
			}
			state.currentItem = { ...action.payload };
		},
		setItemConfigCategoriesData: (state, action: PayloadAction<ItemConfigCategoryOption[]>) => {
			state.itemConfigCategoriesData = action.payload;
		},
		setItemConfigCategoryIds: (state, action: PayloadAction<string[]>) => {
			state.itemConfigCategoryIds = action.payload;
		},
		setItemHandlingUnitMeasure: (state, action: PayloadAction<string>) => {
			state.itemHandlingUnitMeasure = action.payload;
		},
		setSelectedContainerCode: (state, action: PayloadAction<null | string>) => {
			state.selectedContainerCode = action.payload;
		},
		setSelectedContainerConfig: (state, action: PayloadAction<ContainerConfigSummaryFragment | null>) => {
			state.selectedContainerConfig = action.payload;
		},
		setSelectedCustomerItem: (state, action: PayloadAction<SkuUpcMappingFragment | null>) => {
			if (action.payload === null) {
				state.selectedCustomerItem = undefined;
				return;
			}
			state.selectedCustomerItem = { ...action.payload };
		},
		setSelectedItem: (state, action: PayloadAction<ItemSummaryDocument | null>) => {
			state.selectedItem = action.payload;
		},
		setViewType: (state, action: PayloadAction<ViewType>) => {
			state.viewType = action.payload;
		},
		updateAddItemInput: (state, action: PayloadAction<Partial<AddItemInput>>) => {
			if (!action.payload) {
				state.addItemInput = undefined;
			} else {
				if (!state.addItemInput) return;
				state.addItemInput = { ...state.addItemInput, ...action.payload };
			}
		}
	}
});

export const {
	closeCategoryForm,
	closeConfigCategoryForm,
	closeConfigurationRowForm,
	closeContainerForm,
	closeCustomerItemForm,
	closeItemCountForm,
	closeItemForm,
	openCategoryForm,
	openConfigCategoryForm,
	openConfigurationRowForm,
	openContainerForm,
	openCustomerItemForm,
	openItemCountForm,
	openItemForm,
	setAddItemInput,
	setCurrentItem,
	setItemConfigCategoriesData,
	setItemConfigCategoryIds,
	setItemHandlingUnitMeasure,
	setSelectedContainerCode,
	setSelectedContainerConfig,
	setSelectedCustomerItem,
	setSelectedItem,
	setViewType,
	updateAddItemInput
} = productSlice.actions;

export default productSlice.reducer;
